import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import {
  createSampleSetupItem,
  deleteSampleSetupItem,
  getSampleSetupLists,
  updateSampleSetupItem,
} from "../../../../services/sampleSetupService";
import { showErrorAlert } from "../../../ui/utils/AlertUtils";
import { ScreenAnalyses } from "./analysis/Analyses";
import { DestinationAddressSettings } from "./DestinationAddressSettings";
import { AnalysisContainerSettings } from "./AnalysisContainerSettings";

const ENTITIES = [
  {
    key: "visits",
    labelKey: "visit",
    fallbackLabel: "Visit",
    hintKey: "visitsHint",
    fallbackHint: "Sample collection time point",
    nameKey: "visitName",
    fallbackName: "Visit name",
  },
  {
    key: "sampleTypes",
    labelKey: "primaryBackupAbbreviation",
    fallbackLabel: "PRIM / BU",
    hintKey: "sampleTypes",
    fallbackHint: "Sample Types",
    nameKey: "sampleTypeName",
    fallbackName: "Sample type name",
  },
  {
    key: "temperatures",
    labelKey: "temperature",
    fallbackLabel: "Temperature",
    hintKey: "temperaturesHint",
    fallbackHint: "RT, Dry Ice",
    nameKey: "temperatureName",
    fallbackName: "Temperature name",
  },
];

const EMPTY_LISTS = {
  visits: [],
  sampleTypes: [],
  temperatures: [],
};

const EMPTY_DRAFTS = {
  visits: "",
  sampleTypes: "",
  temperatures: "",
};

const sortByName = (items) =>
  [...items].sort((left, right) =>
    String(left.name || "").localeCompare(String(right.name || "")),
  );

export const ScreenSampleSetup = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.user);
  const adminUser = user?.role === "admin";
  const studyId = location.state?.studyId;
  const [lists, setLists] = React.useState(EMPTY_LISTS);
  const [drafts, setDrafts] = React.useState(EMPTY_DRAFTS);
  const [editing, setEditing] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [savingEntity, setSavingEntity] = React.useState(null);

  const loadLists = React.useCallback(async () => {
    if (!studyId) {
      return;
    }

    setIsLoading(true);
    try {
      const nextLists = await getSampleSetupLists(studyId);
      setLists({
        visits: sortByName(nextLists.visits || []),
        sampleTypes: sortByName(nextLists.sampleTypes || []),
        temperatures: sortByName(nextLists.temperatures || []),
      });
    } catch (error) {
      console.error("Failed to load sample setup lists:", error);
      showErrorAlert(
        t("sampleSetupLoadFailed", "Failed to load sample setup lists."),
      );
    } finally {
      setIsLoading(false);
    }
  }, [studyId, t]);

  React.useEffect(() => {
    if (!studyId) {
      navigate("/studies", { replace: true });
      return;
    }

    loadLists();
  }, [loadLists, navigate, studyId]);

  const updateDraft = (entity, value) => {
    setDrafts((currentDrafts) => ({
      ...currentDrafts,
      [entity]: value,
    }));
  };

  const replaceListItem = (entity, item) => {
    setLists((currentLists) => ({
      ...currentLists,
      [entity]: sortByName(
        currentLists[entity].some(
          (currentItem) => String(currentItem.id) === String(item.id),
        )
          ? currentLists[entity].map((currentItem) =>
              String(currentItem.id) === String(item.id) ? item : currentItem,
            )
          : [...currentLists[entity], item],
      ),
    }));
  };

  const removeListItem = (entity, id) => {
    setLists((currentLists) => ({
      ...currentLists,
      [entity]: currentLists[entity].filter(
        (item) => String(item.id) !== String(id),
      ),
    }));
  };

  const createItem = async (entity) => {
    const name = drafts[entity].trim();
    if (!name) {
      showErrorAlert(t("nameRequired", "Name is required."));
      return;
    }

    setSavingEntity(entity);
    try {
      const createdItem = await createSampleSetupItem(entity, {
        name,
        studyId,
      });
      replaceListItem(entity, createdItem);
      updateDraft(entity, "");
    } catch (error) {
      console.error("Failed to create sample setup item:", error);
      showErrorAlert(t("sampleSetupCreateFailed", "Failed to create item."));
    } finally {
      setSavingEntity(null);
    }
  };

  const saveEdit = async () => {
    const name = editing?.name?.trim();
    if (!editing || !name) {
      showErrorAlert(t("nameRequired", "Name is required."));
      return;
    }

    setSavingEntity(editing.entity);
    try {
      const updatedItem = await updateSampleSetupItem(
        editing.entity,
        editing.id,
        {
          name,
          studyId,
        },
      );
      replaceListItem(editing.entity, updatedItem);
      setEditing(null);
    } catch (error) {
      console.error("Failed to update sample setup item:", error);
      showErrorAlert(t("sampleSetupUpdateFailed", "Failed to update item."));
    } finally {
      setSavingEntity(null);
    }
  };

  const deleteItem = async (entity, item) => {
    const result = await Swal.fire({
      text: t(
        "sampleSetupDeleteConfirmation",
        "Are you sure you want to delete this item?",
      ),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("delete", "Delete"),
      cancelButtonText: t("cancel", "Cancel"),
      confirmButtonColor: "#d32f2f",
      reverseButtons: true,
    });

    if (!result.isConfirmed) {
      return;
    }

    setSavingEntity(entity);
    try {
      await deleteSampleSetupItem(entity, item.id);
      removeListItem(entity, item.id);
    } catch (error) {
      console.error("Failed to delete sample setup item:", error);
      showErrorAlert(t("sampleSetupDeleteFailed", "Failed to delete item."));
    } finally {
      setSavingEntity(null);
    }
  };

  if (!adminUser) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="text.secondary">
          {t(
            "sampleSetupAdminOnly",
            "Only administrators can manage sample setup lists.",
          )}
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={2} sx={{ px: 3, pt: 0, pb: 3 }}>
      <ScreenAnalyses
        tableHeight="60vh"
        hideReferenceDescription
        showHeaderAddButton
        sectionTitle={t("analysis", "Analysis")}
      />
      <AnalysisContainerSettings studyId={studyId} />
      <Grid item xs={12}>
        <Typography variant="h6">{t("settings", "Settings")}</Typography>
      </Grid>
      {ENTITIES.map((entityConfig) => {
        const entity = entityConfig.key;
        const isEntitySaving = savingEntity === entity;

        return (
          <Grid item xs={12} md={4} key={entity} sx={{ minWidth: 0 }}>
            <Paper
              sx={{
                p: 2,
                borderRadius: 1,
                height: "100%",
              }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6">
                  {t(entityConfig.labelKey, entityConfig.fallbackLabel)}
                </Typography>
                {entityConfig.hintKey && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.25 }}>
                    {t(entityConfig.hintKey, entityConfig.fallbackHint)}
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  mb: 2,
                  p: 1,
                  borderRadius: 1,
                  bgcolor: "grey.50",
                }}>
                <TextField
                  size="small"
                  fullWidth
                  label={t(entityConfig.nameKey, entityConfig.fallbackName)}
                  value={drafts[entity]}
                  disabled={isEntitySaving}
                  onChange={(event) => updateDraft(entity, event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      createItem(entity);
                    }
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  disabled={isEntitySaving}
                  onClick={() => createItem(entity)}>
                  {t("add", "Add")}
                </Button>
              </Box>

              <Divider />

              <List
                dense
                disablePadding
                sx={{
                  width: "100%",
                  maxHeight: 280,
                  overflowY: "auto",
                  overflowX: "hidden",
                }}>
                {(lists[entity] || []).map((item) => {
                  const isEditing =
                    editing?.entity === entity &&
                    String(editing.id) === String(item.id);

                  return (
                    <ListItem
                      key={item.id}
                      disableGutters
                      secondaryAction={
                        isEditing ? (
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <IconButton
                              edge="end"
                              size="small"
                              aria-label={t("save")}
                              disabled={isEntitySaving}
                              onClick={saveEdit}>
                              <SaveIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              edge="end"
                              size="small"
                              aria-label={t("cancel")}
                              disabled={isEntitySaving}
                              onClick={() => setEditing(null)}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <IconButton
                              edge="end"
                              size="small"
                              aria-label={t("edit")}
                              disabled={isEntitySaving}
                              onClick={() =>
                                setEditing({
                                  entity,
                                  id: item.id,
                                  name: item.name || "",
                                })
                              }>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              edge="end"
                              size="small"
                              aria-label={t("delete")}
                              disabled={isEntitySaving}
                              onClick={() => deleteItem(entity, item)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )
                      }>
                      {isEditing ? (
                        <TextField
                          size="small"
                          fullWidth
                          value={editing.name}
                          disabled={isEntitySaving}
                          sx={{ mr: 9 }}
                          onChange={(event) =>
                            setEditing((currentEditing) => ({
                              ...currentEditing,
                              name: event.target.value,
                            }))
                          }
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              saveEdit();
                            }
                          }}
                        />
                      ) : (
                        <ListItemText
                          primary={item.name}
                          primaryTypographyProps={{
                            sx: {
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              pr: 9,
                            },
                          }}
                        />
                      )}
                    </ListItem>
                  );
                })}
                {lists[entity]?.length === 0 && (
                  <ListItem disableGutters>
                    <ListItemText
                      primary={t("noItems", "No items")}
                      primaryTypographyProps={{ color: "text.secondary" }}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>
        );
      })}
      <DestinationAddressSettings studyId={studyId} />
    </Grid>
  );
};
